import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import { isNull, omit } from 'lodash';
import { scroller } from 'react-scroll';

import { productActions } from '../../store/modules/product';
import { appActions } from '../../store/modules/app';
import { setProductDeliveryTypeInfo } from '../../lib/utils/productUtils';
import {
  asyncSimpleModal,
  calculatePoint,
  reDirectPage,
  setIfNull,
  setNumberWithComma,
  setRequestForm,
  setTrim,
} from '../../lib/common';
import { setDateFormat } from '../../lib/moment';
import ProductApi from '../../lib/api/product/ProductApi';
import useToastMsg from '../common/useToastMsg';
import useCustomFieldsUpdate from './useCustomFieldsUpdate';
import { isProductOptionValidForGift, isProductOptionValidForTodayDelivery } from '../../views/product/common/utils';

import {
  AVAILABLE_CHAIN_SHAPE_TAG_CATEGORY_NAME_LIST,
  EXTERNAL_CHANNEL,
  PRODUCT_CATEGORY_NAME,
  PRODUCT_DETAIL_PAGE_PANEL_NAME,
  PRODUCT_EXTERNAL_ACTION_TYPE,
  PRODUCT_MANAGEMENT_TYPE,
  PRODUCT_MINIMUM_STOCK,
  PRODUCT_OPTION_STATUS,
  PRODUCT_PROPERTY_KEY,
  PRODUCT_STATUS,
  PRODUCT_UNLIMITED_STOCK_VALUE,
} from '../../constants/service/app/productConstants';
import { PAY_METHOD_TYPE } from '../../constants/service/app/orderConstants';
import { BRAND_TYPE } from '../../constants/service/app/brandConstants';
import { DATE_FORMAT } from '../../constants/service/common/utilConstants';
import productToastMsg from '../../constants/service/toastMsg/productToastMsg';
import { PRODUCT_ALL_LIST_URL_PATH } from '../../constants/service/common/urlConstants';
import { PRODUCT_TAG_AND_PROPERTY_KIND } from '../../constants/service/app/tagConstants';
import { PRODUCT_RES_CODE } from '../../constants/responseCode/productResCode';

const useSubmit = (form, initValue, customAttributeKeys = []) => {
  const toastMsg = useToastMsg();
  const dispatch = useDispatch();
  const {
    listPageUrl,
    productList: { searchData, searchOption, filterOption, sortOption, pagination },
  } = useSelector((state) => state.product);

  const { updateCustomAttributes } = useCustomFieldsUpdate(initValue.productId, customAttributeKeys);

  /**
   * form 데이터 유효성 체크
   */
  const validateCheck = useCallback(
    (callback) => {
      form.validateFieldsAndScroll({ scroll: { offsetTop: 200, offsetBottom: 200 } }, async (err, value) => {
        if (err) {
          // 상품 커스텀 속성 에러 체크 (기존 form 필드가 아닌 key들)
          const hasCustomAttributeError = Object.keys(err).some((key) => customAttributeKeys.includes(key));

          if (hasCustomAttributeError) {
            // 커스텀 속성 섹션으로 스크롤
            scroller.scrollTo(PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_CUSTOM_ATTRIBUTE, {
              duration: 500,
              smooth: true,
              offset: -100,
            });
          }
          return;
        }

        /**
         *  상품 최소값
         */
        const minimumPrice = initValue.proCategory.minimumPrice;

        /**
         *  옵션 리스트
         */
        let hasOptionList = false;
        if (value.isOption) {
          if (!value.optionList?.length) {
            message.error('상품 옵션을 추가해주시기 바랍니다');
            scroller.scrollTo(PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_STOCK_AND_OPTION, { offset: 200 });
            return;
          }

          hasOptionList = true;
        }
        if (hasOptionList) {
          if (value.optionKeyName === null) {
            message.error('옵션명을 입력하시기 바랍니다.');
            scroller.scrollTo(PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_STOCK_AND_OPTION, { offset: 500 });
            return;
          }

          /**
           *  상품 옵션 유효성 검사
           */
          let isInvalid = false;

          value.optionList.forEach((item) => {
            if (!isInvalid) {
              /**
               *  옵션가 유효성 검사
               */
              if (item.price < minimumPrice - value.salesPrice) {
                isInvalid = true;
                message.error(`최소 옵션가는 ${setNumberWithComma(minimumPrice - value.salesPrice, '원')}`);
                scroller.scrollTo(PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_STOCK_AND_OPTION, { offset: 500 });
              }

              /**
               *  재고 유효성 검사
               */
              if (item.status === PRODUCT_OPTION_STATUS.ACTIVE && item.stockCount === PRODUCT_MINIMUM_STOCK) {
                isInvalid = true;
                message.error('옵션 상태가 노출(판매중)인 경우 재고 수량은 무제한(빈값)이거나 1 이상이어야 합니다.');
                scroller.scrollTo(PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_STOCK_AND_OPTION, { offset: 500 });
              }
            }
          });

          if (!isInvalid) {
            const styleCodeList = value.optionList.reduce((acc, item) => {
              const styleCode = setIfNull(setTrim(item.styleCode));
              if (styleCode) acc.push(styleCode);
              return acc;
            }, []);
            const uniqStyleCodeList = [...new Set(styleCodeList)];

            /**
             *  스타일 코드 중복 검사
             */
            if (styleCodeList.length !== uniqStyleCodeList.length) {
              isInvalid = true;
              message.error('상품 옵션 리스트의 스타일 코드가 중복됩니다.');
              scroller.scrollTo(PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_STOCK_AND_OPTION, { offset: 500 });
            }
          }

          if (isInvalid) return;
        }

        /**
         *  선물여부
         */
        const isGift = hasOptionList
          ? !!value.optionList.find((item) => isProductOptionValidForGift(item))
          : value.feeInfo.isGift;

        /**
         *  결제 가능수단이 유효하지 않은지 여부
         *  isGift = true 인데 결제가능수단이 가상계좌 밖에 없는 경우
         */
        let isInvalidProductPaymentMethod = false;

        if (isGift) {
          isInvalidProductPaymentMethod = !value.productPayMethodList.includes(PAY_METHOD_TYPE.VBANK);
        }

        if (isNull(value.deliveryType)) {
          /**
           *  상품 배송 방법 유효성 검사
           */
          scroller.scrollTo(PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_DELIVERY_INFO);
          return;
        } else if (value.salesPrice < minimumPrice) {
          /**
           *  실판매가 유효성 검사
           */
          message.error(`판매가는 ${minimumPrice}원 이상이어야 합니다.`);
          scroller.scrollTo(PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_NAME_AND_PRICE);
          return;
        } else if (
          /**
           *  재고 유효성 검사
           */
          !value.isOption &&
          value.status === PRODUCT_STATUS.NOW_ON_SALE &&
          value.feeInfo.stockCount === PRODUCT_MINIMUM_STOCK
        ) {
          message.error('상품 상태가 판매중인 경우 재고 수량은 무제한(빈값)이거나 1 이상이어야 합니다.');
          scroller.scrollTo(PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_STOCK_AND_OPTION);
          return;
        } else if (isInvalidProductPaymentMethod) {
          /**
           *  결재 타입 검증
           */
          message.error('결제 가능한 수단이 없습니다. 상품 결제 타입 또는 선물 여부를 변경해주세요.');
          scroller.scrollTo(PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_PAY_METHOD);
          return;
        }

        /**
         *  옵션 리스트
         */
        let optionList = hasOptionList
          ? value.optionList.map((item) => ({
              ...item,
              price: setIfNull(item.price, null, 0),
              styleCode: setIfNull(setTrim(item.styleCode)),
              isTodayDelivery: Number(item.productManagementType !== PRODUCT_MANAGEMENT_TYPE.ENTER),
              isGift: Number(item.isGift),
            }))
          : [];

        /**
         *  모든 옵션이 비노출로 설정할 경우에는 등록/편집/복사가 되지 않아야 함
         */
        const inActiveOptionList = optionList.filter((option) => option.status === PRODUCT_OPTION_STATUS.IN_ACTIVE);
        if (optionList.length && optionList.length === inActiveOptionList.length) {
          await asyncSimpleModal({
            type: 'error',
            title: '옵션 설정',
            message: '모든 옵션을 비노출로 설정할 수 없습니다.',
          });

          scroller.scrollTo(PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_STOCK_AND_OPTION, { offset: 500 });
          return;
        }

        /**
         * 상품 상태를 판매중지로 변경을할 경우 앱에서 노출이 되지 않을 수 있다는 경고 모달 보여주기
         */
        if (value.status === PRODUCT_STATUS.SALES_STOP) {
          const confirmResult = await asyncSimpleModal({
            type: 'confirm',
            title: '상품 판매중지',
            message: '상품 상태를 판매중지로 변경하면 앱에 노출이 안됩니다. 그래도 진행하시겠습니까?',
          });

          if (!confirmResult) {
            scroller.scrollTo(PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_STATUS_AND_BRAND_INFO, { offset: -200 });
            return;
          }
        }

        callback(value);
      });
    },
    [form, initValue, customAttributeKeys],
  );

  /**
   * form 데이터를 submit 에 필요한 데이터 형태로 포맷
   */
  const formatSubmitData = useCallback(
    (formValue) => {
      /**
       *  옵션 리스트 존재 여부
       */
      const hasOptionList = formValue.isOption && formValue.optionList && formValue.optionList.length;

      /**
       *  선물여부
       */
      const isGift = hasOptionList
        ? !!formValue.optionList.find((item) => isProductOptionValidForGift(item))
        : formValue.feeInfo.isGift;

      /**
       *  옵션 리스트
       */
      let optionList = hasOptionList
        ? formValue.optionList.map((item) => ({
            ...item,
            name: setTrim(item.name),
            price: setIfNull(item.price, null, 0),
            styleCode: setIfNull(setTrim(item.styleCode)),
            // 옵션리스트 상에서는 품절이더라도 오늘 출발 뱃지를 노출할 수 있어야함.
            isTodayDelivery: Number(item.productManagementType !== PRODUCT_MANAGEMENT_TYPE.ENTER),
            isGift: Number(item.isGift),
          }))
        : [];

      /**
       *  상품 배송 방법,
       *  주문 시 제작 제품 여부,
       *  주문 시 제작 소요시간,
       *  바로 배송 기준 시간 설정하기
       */
      const { deliveryType, isOrderMade, orderMadeDate, directDeliveryTimeLimit } = setProductDeliveryTypeInfo({
        deliveryType: formValue.deliveryType,
        isOrderMade: formValue.isOrderMade,
        orderMadeDate: formValue.orderMadeDate,
        directDeliveryTimeLimit: formValue.directDeliveryTimeLimit,
      });

      /**
       *  상품 수수료 리스트
       */
      let feeList = [];
      if (!formValue.isOption) {
        feeList.push({
          ...omit(formValue.feeInfo, ['productManagementTypeSelectList']),
          productOptionId: null,
          management:
            formValue.feeInfo.productManagementType === PRODUCT_MANAGEMENT_TYPE.ENTER
              ? BRAND_TYPE.BRAND
              : BRAND_TYPE.AMONDZ,
          stockCount: isNull(formValue.feeInfo.stockCount)
            ? PRODUCT_UNLIMITED_STOCK_VALUE
            : formValue.feeInfo.stockCount,
          isPgFee: Number(formValue.feeInfo.isPgFee),
        });
      } else if (optionList.length) {
        optionList.forEach((item) => {
          feeList.push({
            productManagementType: item.productManagementType,
            management:
              item.productManagementType === PRODUCT_MANAGEMENT_TYPE.ENTER ? BRAND_TYPE.BRAND : BRAND_TYPE.AMONDZ,
            productOptionId: item.id,
            stockCount: isNull(item.stockCount) ? PRODUCT_UNLIMITED_STOCK_VALUE : item.stockCount,
            isPgFee: Number(item.feeInfo.isPgFee),
            amondzFeeRate: item.feeInfo.amondzFeeRate,
            marketingFeeRate: item.feeInfo.marketingFeeRate,
            fulfillmentFeeRate: item.feeInfo.fulfillmentFeeRate,
          });
        });
      }

      optionList = optionList.map((item, index) => ({
        ...omit(item, [
          'isNew',
          'stockCount',
          'productManagementTypeSelectList',
          'productManagementType',
          'isShowFeeInfo',
          'feeInfo',
        ]),
        no: index + 1,
      }));

      /**
       * 카테고리 리스트 및 카테고리 업데이트 로그 리스트 맵핑
       */
      const productCategoryUpdateLogIdList = [];
      const categoryList = formValue.categoryList.map((main) => {
        productCategoryUpdateLogIdList.push({
          id: main.id,
          productCategoryUpdateLogId: main.productCategoryUpdateLogId,
          name: main.name,
        });
        const subCategoryId = main.subCategory.map((sub) => {
          productCategoryUpdateLogIdList.push({
            id: sub.id,
            productCategoryUpdateLogId: sub.productCategoryUpdateLogId,
            name: sub.name,
          });

          return sub.id;
        });
        return {
          mainCategoryId: main.id,
          mainCategoryName: main.name,
          subCategoryId,
        };
      });

      /**
       * [상품 속성] 노출 중인 속성이 아닌 경우 list 에 빈배열을 넣어줌
       */
      const productPropertyList = { ...formValue.productPropertyList };
      for (const key in formValue.productPropertyList) {
        // 주요소재>기타소재가 선택되어 있지 않을 경우
        // productPropertyList 에 기타소재 속성을 넣지 않음
        const isOtherMaterial =
          key === PRODUCT_PROPERTY_KEY.OTHER_MATERIAL &&
          !formValue.productPropertyList.mainMaterial.list.find((item) => item.name === '기타소재');

        // 카테고리가 귀걸이가 아니면서 침소재 속성을 가지고 있는 경우
        // productPropertyList 에 침소재 속성을 넣지 않음
        const isAcupunctureMaterial =
          key === PRODUCT_PROPERTY_KEY.ACUPUNCTURE_MATERIAL &&
          !categoryList.find((item) => item.mainCategoryName === PRODUCT_CATEGORY_NAME.EARRING);

        // 카테고리가 목걸이/팔찌/발찌가 아니면서 체인모양 속성을 가지고 있는 경우
        // productPropertyList 에 체인모양 속성을 넣지 않음
        const isChainShape =
          key === PRODUCT_PROPERTY_KEY.CHAIN_SHAPE &&
          !categoryList.find((item) => AVAILABLE_CHAIN_SHAPE_TAG_CATEGORY_NAME_LIST.includes(item.mainCategoryName));

        if (isOtherMaterial || isAcupunctureMaterial || isChainShape) {
          productPropertyList[key] = { ...productPropertyList[key], list: [] };
        }
      }

      return {
        productId: initValue.productId,
        /**
         *  상품 로그 id
         */
        productUpdateLogId: initValue.productUpdateLogId,
        /**
         *  상품 상세
         */
        productInfo: {
          /**
           *  A/S 안내
           */
          asInform: formValue.asInform,
          /**
           *  카테고리 리스트
           */
          categoryList,
          /**
           *  착용 시 주의 사항
           */
          caution: formValue.caution,
          /**
           *  상품 배송 타입
           */
          deliveryType,
          /**
           *  바로 배송 기준 시간
           */
          directDeliveryTimeLimit,
          /**
           *  할인액
           */
          discountAmount: formValue.isDiscount ? formValue.discountAmount : null,
          /**
           *  할인률
           */
          discountRate: formValue.isDiscount ? formValue.discountRate : null,
          /**
           *  할인 타입 (0: 정액 할인, 1: 정률 할인)
           */
          discountType: formValue.isDiscount ? formValue.discountType : null,
          /**
           *  할인 시작 날짜
           */
          discountStartDate:
            formValue.isDiscount && formValue.isDiscountPeriod && formValue.discountPeriod?.startDate
              ? setDateFormat(formValue.discountPeriod.startDate, DATE_FORMAT.FULL_DASH_FORMAT)
              : null,
          /**
           *  할인 종료 날짜
           */
          discountEndDate:
            formValue.isDiscount && formValue.isDiscountPeriod && formValue.discountPeriod?.endDate
              ? setDateFormat(formValue.discountPeriod.endDate, DATE_FORMAT.FULL_DASH_FORMAT)
              : null,
          /**
           *  선물 여부
           */
          isGift: Number(isGift),
          /**
           *  주문 시 제작 제품 여부
           */
          isOrderMade,
          /**
           *  오늘 출발 여부
           */
          isTodayDelivery: hasOptionList
            ? Number(!!formValue.optionList.find((item) => isProductOptionValidForTodayDelivery(item)))
            : Number(formValue.feeInfo.productManagementType !== PRODUCT_MANAGEMENT_TYPE.ENTER),
          /**
           *  제조사
           */
          manufacturer: formValue.manufacturer,
          /**
           *  상품명
           */
          name: setTrim(formValue.name),
          /**
           *  상품 번호
           */
          number: setIfNull(setTrim(formValue.number)),
          /**
           *  주문 예시 메시지
           */
          orderExampleMessage:
            formValue.orderExampleMessage && formValue.orderExampleMessage.length
              ? formValue.orderExampleMessage
              : null,
          /**
           *  주문 시 제작 소요시간
           */
          orderMadeDate,
          /**
           *  제조국
           */
          origin: formValue.origin,
          /**
           *  판매가
           */
          originPrice: formValue.originPrice,
          /**
           *  상품 이미지
           */
          productAttachment: {
            /**
             *  대표 이미지
             */
            mainImg: formValue.mainImg,
            /**
             *  추가 이미지
             */
            subImg: formValue.subImg,
            /**
             *  상세 이미지
             */
            detailImg: formValue.detailImg,
          },
          /**
           * 상품 카테고리 업데이트 로그 리스트
           */
          productCategoryUpdateLogIdList,
          /**
           *  상품 수수료
           */
          productFeeInfo: {
            feeList,
          },
          /**
           *  상품 옵션
           */
          productOption: {
            /**
             *  상품 옵션 KEY
             */
            optionKeyName: formValue.optionKeyName,
            /**
             *  상품 옵션 리스트
             */
            optionList: optionList,
          },
          /**
           *  상품 결제 타입 ID
           */
          productPayMethodTypeId: formValue.productPayMethodTypeId,
          /**
           * 상품 속성
           */
          productPropertyList,
          /**
           *  상품 태그
           */
          productTagInfo: formValue.productTagInfo,
          /**
           *  품질보증 기준
           */
          qualityAssurance: formValue.qualityAssurance,
          /**
           *  실판매가
           */
          salesPrice: formValue.salesPrice,
          /**
           *  사이즈
           */
          size: formValue.size,
          /**
           *  상품 상태
           */
          status: formValue.status,
          /**
           *  스토어 ID
           */
          storeId: formValue.storeId,
          /**
           *  스타일 코드
           */
          styleCode: setIfNull(setTrim(formValue.styleCode)),
          /**
           *  중량
           */
          weight: formValue.weight,
        },
      };
    },
    [initValue.productId, initValue.productUpdateLogId],
  );

  /**
   * 상품 등록 submit
   */
  const onInsert = () => {
    validateCheck(async (formValue) => {
      const req = formatSubmitData(formValue);
      try {
        const result = await ProductApi.insertProduct(setRequestForm(req, true));
        if (result.msg) {
          toastMsg.error(result.msg);
        } else {
          toastMsg.success(productToastMsg.INSERT_PRODUCT_REQUEST_SUCCESS);

          reDirectPage({ path: PRODUCT_ALL_LIST_URL_PATH });
        }
      } catch (error) {
        toastMsg.error(productToastMsg.INSERT_PRODUCT_REQUEST_ERROR, error);
      }
    });
  };

  /**
   * 상품 편집 submit
   */
  const onEdit = (param) => {
    validateCheck(async (formValue) => {
      const req = formatSubmitData(formValue);

      // 외부 채널 전송
      if (param?.sendProductDataToSabangnet) {
        req.externalRequest = [
          {
            type: PRODUCT_EXTERNAL_ACTION_TYPE.UPDATE,
            channel: EXTERNAL_CHANNEL.SABANGNET,
          },
        ];
      }

      try {
        // 커스텀 필드 업데이트
        const isCustomFieldsUpdateSuccess = await updateCustomAttributes(formValue);
        if (!isCustomFieldsUpdateSuccess) {
          return;
        }

        const result = await ProductApi.editProduct(setRequestForm(req, true));

        if (result.data?.isInvalidTagMsg) {
          await asyncSimpleModal({
            type: 'error',
            title: '유효하지 않은 태그',
            message: result.data.isInvalidTagMsg,
          });
          return;
        }

        // 상품 편집 시 이동할 페이지 정보
        const path = listPageUrl || PRODUCT_ALL_LIST_URL_PATH;
        const state = {
          searchData,
          searchOption,
          filterOption,
          sortOption,
          pagination,
        };
        if (result.msg) {
          /**
           *  상품의 데이터가 이미 수정된 데이터인 경우
           */
          if (result.status === PRODUCT_RES_CODE.ALREADY_EDITED_DATA) {
            await asyncSimpleModal({
              type: 'error',
              title: '상품 편집 에러',
              message: result.msg,
            });

            reDirectPage({ path, state });
          } else {
            toastMsg.error(result.msg);
          }
        } else {
          toastMsg.success(productToastMsg.EDIT_PRODUCT_REQUEST_SUCCESS);
          reDirectPage({ path, state });
        }
      } catch (error) {
        toastMsg.error(productToastMsg.INSERT_PRODUCT_REQUEST_ERROR, error);
      }
    });
  };

  /**
   * 상품 삭제하기
   */
  const deleteProduct = () => {
    const { productId, productUpdateLogId } = initValue;

    dispatch(
      productActions.toggleModal({
        name: 'deleteProductModal',
        value: true,
        data: {
          productId,
          productUpdateLogId,
        },
      }),
    );
  };

  /**
   * 판매 승인하기
   * @param {sendProductDataToSabangnet: boolean} - 사방넷 송신 여부
   * @returns {Promise<void>}
   */
  const permitProduct = async (param) => {
    const { productId, productUpdateLogId, proStockAndOption } = initValue;

    /**
     *  재고 수량이 0인 경우 상품 승인불가
     */
    const { isOption, optionList, feeInfo } = proStockAndOption;
    if ((!isOption && feeInfo.stockCount === 0) || (isOption && optionList.find((item) => item.stockCount === 0))) {
      await asyncSimpleModal({
        type: 'error',
        title: '상품 승인 불가',
        message: '재고 수량이 0인 경우에 상품 승인이 불가능합니다.',
      });
      return;
    }

    const externalRequests = [];

    // 외부 채널 전송
    if (param?.sendProductDataToSabangnet) {
      externalRequests.push({
        type: PRODUCT_EXTERNAL_ACTION_TYPE.REGISTER,
        channel: EXTERNAL_CHANNEL.SABANGNET,
      });
    }

    dispatch(
      productActions['permitRegistrationProduct']({
        productId,
        productUpdateLogId,
        externalRequest: externalRequests,
      }),
    );
  };

  /**
   * 판매 금지된 상품 승인하기
   * @returns {Promise<void>}
   */
  const permitBanProduct = async () => {
    const { productId, productUpdateLogId } = initValue;

    const confirmResult = await asyncSimpleModal({
      type: 'confirm',
      title: '상품 금지 승인',
      message: '금지된 상품을 승인하시겠습니까?',
    });

    if (!confirmResult) return;

    dispatch(
      productActions['permitBanProduct']({
        productId,
        productUpdateLogId,
      }),
    );
  };

  /**
   * 판매 거절하기
   * @param type - ban: 판매 금지 상태에서 판매 거절, registration: 승인 대기 상태에서 판매 거절
   */
  const rejectProduct = (type) => {
    const { productId, productUpdateLogId } = initValue;

    let name;
    if (type === 'ban') {
      name = 'rejectBanProductModal';
    } else if (type === 'registration') {
      name = 'rejectRegistrationProduct';
    }

    dispatch(
      productActions.toggleModal({
        name,
        value: true,
        data: {
          productId,
          productUpdateLogId,
        },
      }),
    );
  };

  /**
   * 거절 사유 보기
   */
  const getRejectReasonList = (productId) => {
    dispatch(productActions.getRejectReasonList(productId));
  };

  /**
   * 미리보기
   */
  const openPreview = () => {
    const formValue = form.getFieldsValue();

    /**
     * 새로 추가된 상품 속성, 태그 정보를 기존의 태그 정보로 포맷
     */
    let tagLinkData = {};
    for (const key in formValue.productPropertyList) {
      tagLinkData[PRODUCT_TAG_AND_PROPERTY_KIND[key]] = formValue.productPropertyList[key].list.map(
        (item) => item.name,
      );
    }
    for (const key in formValue.productTagInfo) {
      tagLinkData[PRODUCT_TAG_AND_PROPERTY_KIND[key]] = formValue.productTagInfo[key].list.map((item) => item.name);
    }
    const data = {
      /**
       *  상품 상태
       */
      status: formValue.status,
      /**
       *  상점 상태
       *  카테고리 변경 시 productDetail.proStatusAndBrandInfo.storeStatus 에 직접 값 넣어줌
       */
      storeStatus: initValue.proStatusAndBrandInfo.storeStatus,
      /**
       *  1,2차 카테고리명 리스트
       */
      categoryNameList: formValue.categoryList.reduce((acc, item) => {
        acc.push(item.name);
        item.subCategory.forEach(({ name }) => {
          acc.push(name);
        });
        return acc;
      }, []),
      /**
       *  1차 카테고리명 리스트
       */
      mainCategoryNameList: formValue.categoryList.map((item) => item.name),
      /**
       *  상품명
       */
      name: formValue.name,
      /**
       *  할인 타입
       */
      discountType: formValue.discountType,
      /**
       *  판매가
       */
      salesPrice: formValue.salesPrice,
      /**
       *  원가
       */
      originPrice: formValue.originPrice,
      /**
       *  할인율
       */
      discountRate: formValue.discountRate,
      /**
       *  할인 시작일
       */
      discountStartDate:
        formValue.isDiscount &&
        formValue.isDiscountPeriod &&
        formValue.discountPeriod &&
        formValue.discountPeriod.startDate,
      /**
       *  할인 종료일
       */
      discountEndDate:
        formValue.isDiscount &&
        formValue.isDiscountPeriod &&
        formValue.discountPeriod &&
        formValue.discountPeriod.endDate,
      /**
       *  적립될 포인트
       */
      savingPoint: calculatePoint(formValue.salesPrice),
      /**
       *  착용 시 주의사항
       */
      caution: formValue.caution,
      /**
       *  중량
       */
      weight: formValue.weight,
      /**
       *  사이즈
       */
      size: formValue.size,
      /**
       *  제조국
       */
      origin: formValue.origin,
      /**
       *  제조자
       */
      manufacturer: formValue.manufacturer,
      /**
       *  a/s 안내
       */
      asInform: formValue.asInform,
      /**
       *  품질보증 기준
       */
      qualityAssurance: formValue.qualityAssurance,
      /**
       *  매장명
       */
      storeName: initValue.proStatusAndBrandInfo.brandName,
      /**
       *  고객센터 전화번호(매장번호)
       */
      storePhone: initValue.proStatusAndBrandInfo.storePhone,
      /**
       *  반품 / 교환 정보
       */
      memo: initValue.proDeliveryInfo.returnAddress.memo,
      /**
       *  교환 및 반품 시 보낼 곳 주소 1
       */
      line1: initValue.proDeliveryInfo.returnAddress.line1,
      /**
       *  교환 및 반품 시 보낼 곳 주소 2
       */
      line2: initValue.proDeliveryInfo.returnAddress.line2,
      /**
       *  고객센터 전화번호(a/s 전화번호)
       */
      asPhone: initValue.proAfterService.asPhone,
      /**
       *  카카오톡 플러스 친구
       */
      kakaoAccount: initValue.proAfterService.kakaoAccount,
      /**
       *  제주도 추가 배송비
       */
      deliveryChargeJeju: initValue.proStatusAndBrandInfo.deliveryChargeJeju,
      /**
       *  그 외 도서산간 지역 추가 배송비
       */
      deliveryChargeEtc: initValue.proStatusAndBrandInfo.deliveryChargeEtc,
      /**
       *  제품 메인 이미지
       */
      mainImg: formValue.mainImg,
      /**
       *  제품 추가 이미지
       */
      subImg: formValue.subImg,
      /**
       *  제품 상세 이미지
       */
      detailImg: formValue.detailImg,
      /**
       *  제품 태그 목록
       */
      tagLinkData,
      /**
       *  상품 공지사항 정보
       */
      productNotice: {
        content: initValue.proNoticeProduct.list[0] && initValue.proNoticeProduct.list[0].content,
        startDate: initValue.proNoticeProduct.list[0] && initValue.proNoticeProduct.list[0].startDate,
        endDate: initValue.proNoticeProduct.list[0] && initValue.proNoticeProduct.list[0].endDate,
      },
      /**
       *  상품 배송 타입
       */
      deliveryType: formValue.deliveryType,
      /**
       *  주문 시 제작 제품 여부
       */
      isOrderMade: formValue.isOrderMade,
      /**
       *  주문 시 제작 소요시간 (1 ~ 14)
       */
      orderMadeDate: formValue.orderMadeDate,
      /**
       *  바로 배송 기준 시간 (0 ~ 23)
       */
      directDeliveryTimeLimit: formValue.directDeliveryTimeLimit,
      /**
       *  상품 좋아요 개수
       */
      likeCount: initValue.likeCount,
      /**
       *  오늘 출발 여부
       */
      isTodayDelivery: formValue.isOption
        ? !!formValue.optionList.find((item) => isProductOptionValidForTodayDelivery(item))
        : formValue.feeInfo.productManagementType !== PRODUCT_MANAGEMENT_TYPE.ENTER,
      /**
       *  옵션 리스트
       */
      optionList: formValue.isOption
        ? formValue.optionList.reduce((acc, item) => {
            acc.push({
              ...item,
              // 옵션리스트 상에서는 품절이더라도 오늘 출발 뱃지를 노출할 수 있어야함.
              isTodayDelivery: item.productManagementType !== PRODUCT_MANAGEMENT_TYPE.ENTER,
            });
            return acc;
          }, [])
        : [],
    };

    dispatch(
      appActions.appToggleModal({
        name: 'productDetailPreviewModal',
        value: true,
        data: data,
      }),
    );
  };

  return {
    onInsert,
    onEdit,
    deleteProduct,
    permitProduct,
    permitBanProduct,
    rejectProduct,
    getRejectReasonList,
    openPreview,
  };
};

export default useSubmit;
